import { DEFAULT_SHAPE_SIZES } from '@/lib/shapes/constants/default-shape-sizes'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { mirrorPathCommands, shapeToPathCommands } from '@/lib/elements/shape-path-commands'
import { rotatedGeometricAabbOfShape } from '@/lib/shapes/shape-coordinates'
import { pointerDragThresholdCanvas } from '@/lib/text/text-tool-create'
import type { DragCreateState } from '../types/drag-create-state'

const MIN_DIMENSION_PX = 10

/** Rectangle intersection for marquee selection (same behavior as spread). */
export function rectsIntersect(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number }
): boolean {
  return (
    a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y
  )
}

/**
 * Shape AABB for marquee hit-test (stroke-aware, rotation-aware).
 *
 * Rotates about the geometric center (`rotatedGeometricAabbOfShape` — hit-testing is a geometric
 * concern, matching the "gesture math stays geometric" rule elsewhere in this codebase), then
 * pads by half the stroke width per side. Previously used the raw unrotated `s.x/y/width/height`
 * directly, which is a strict subset of the true rotated footprint for any non-axis-aligned
 * rotation — a false negative, never a false positive: a marquee that visually overlapped a
 * rotated shape's corner (outside the unrotated box, inside the rotated one) could fail to select
 * it, but the old box never over-selected. Marquee hit-testing is already an AABB approximation
 * (not an exact silhouette test), so this stays conservative in the same way, just no longer
 * blind to rotation.
 */
export function computeShapeMarqueeAabb(s: ShapeData): {
  x: number
  y: number
  width: number
  height: number
} {
  const sw = s.strokeWidth ?? 1
  const rotated = rotatedGeometricAabbOfShape(s)

  return {
    x: rotated.x - sw / 2,
    y: rotated.y - sw / 2,
    width: rotated.width + sw,
    height: rotated.height + sw,
  }
}

/**
 * Computes shape creation params for both final creation and drag preview. `scale` is the live
 * canvas zoom, read by the caller at call time (it sizes the click-vs-drag threshold).
 */
export function computeShapeCreationGeometry(
  drag: DragCreateState,
  shapeType: ShapeType,
  mode: 'create' | 'preview',
  scale: number
): {
  x: number
  y: number
  width: number
  height: number
  anchor?: 'center' | 'top-left'
  anchorPoint?: { x: number; y: number }
  mirror?: { x: boolean; y: boolean }
} {
  const {
    startCanvasX: startX,
    startCanvasY: startY,
    currentCanvasX: endX,
    currentCanvasY: endY,
    shiftKey,
    altKey,
  } = drag
  const dx = endX - startX
  const dy = endY - startY
  const absDx = Math.abs(dx)
  const absDy = Math.abs(dy)
  const dragThreshold = pointerDragThresholdCanvas(scale)
  const isDrag = absDx >= dragThreshold || absDy >= dragThreshold
  const defaults = DEFAULT_SHAPE_SIZES[shapeType] ?? { width: 72, height: 72 }
  const mirror = {
    x: dx < 0,
    y: dy < 0,
  }

  if (isDrag) {
    if (altKey) {
      // Center-origin: start is center, shape expands equally in all directions
      let w = Math.max(MIN_DIMENSION_PX, 2 * absDx)
      let h = Math.max(MIN_DIMENSION_PX, 2 * absDy)

      if (shiftKey) {
        const size = Math.max(MIN_DIMENSION_PX, 2 * Math.max(absDx, absDy))

        w = size
        h = size
      }

      const centerAnchor = { x: w / 2, y: h / 2 }

      return {
        x: mode === 'create' ? startX : startX - w / 2,
        y: mode === 'create' ? startY : startY - h / 2,
        width: w,
        height: h,
        anchorPoint: mode === 'create' ? centerAnchor : undefined,
      }
    }

    // Bounding box = rectangle between mouse-down and mouse-up (no path-anchor locking).
    let w = Math.max(MIN_DIMENSION_PX, absDx)
    let h = Math.max(MIN_DIMENSION_PX, absDy)
    let effectiveEndX = endX
    let effectiveEndY = endY

    if (shiftKey) {
      const size = Math.max(MIN_DIMENSION_PX, Math.max(absDx, absDy))
      const signX = dx >= 0 ? 1 : -1
      const signY = dy >= 0 ? 1 : -1

      w = size
      h = size
      effectiveEndX = startX + signX * size
      effectiveEndY = startY + signY * size
    }

    const boxLeft = Math.min(startX, effectiveEndX)
    const boxTop = Math.min(startY, effectiveEndY)

    return {
      x: boxLeft,
      y: boxTop,
      width: w,
      height: h,
      anchor: 'top-left',
      mirror,
    }
  }

  // Click (no drag): default-sized shape; when altKey, center at start point
  let w = defaults.width
  let h = defaults.height

  if (shiftKey) {
    const size = Math.max(defaults.width, defaults.height)

    w = size
    h = size
  }

  const centerAnchor = { x: w / 2, y: h / 2 }

  return {
    x: mode === 'create' ? startX : startX - w / 2,
    y: mode === 'create' ? startY : startY - h / 2,
    width: w,
    height: h,
    anchorPoint: mode === 'create' && altKey ? centerAnchor : undefined,
    mirror,
  }
}

/** Preset path commands for a new shape, mirrored when the drag ran up and/or left. */
export function buildMirroredShapePathCommands(
  shapeType: ShapeType,
  width: number,
  height: number,
  mirror?: { x: boolean; y: boolean }
) {
  const base = shapeToPathCommands(shapeType, width, height)

  if (!mirror) return base

  return mirrorPathCommands(base, mirror, { width, height })
}
